import { Injectable, InjectionToken, inject } from '@angular/core';

import { parseJsonString } from '../helpers';
import { PlatformService } from '../platform/platform.service';

import { StorageCleanupService } from './storage-cleanup.service';
import {
  KeysToCleanup,
  STORAGE_CLEANUP_CONFIG_KEY,
  StorageConfig,
  StorageHelperService,
  getAllPrefixedKeys,
  getPrefixedKey,
  prepareStorageValue
} from './storage-helper.service';

export const STORAGE_PREFIX = new InjectionToken<string>('core-util.storage-prefix');
export const STORAGE_LOGOUT_CLEANUP_KEYS = new InjectionToken<KeysToCleanup>('core-util.storage-logout-cleanup-keys');
export const STORAGE_ACCOUNT_SWITCH_CLEANUP_KEYS = new InjectionToken<KeysToCleanup>(
  'core-util.storage-accountSwitch-cleanup-keys'
);
export const STORAGE_EXCEPTIONAL_KEY_REGEX = new InjectionToken<RegExp>('core-util.storage-exceptional-keys');

export enum StorageType {
  LOCAL = 'localStorage',
  SESSION = 'sessionStorage'
}

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private readonly prefix = inject(STORAGE_PREFIX, { optional: true });

  private readonly storageHelper = inject(StorageHelperService);
  private readonly storageCleanupService = inject(StorageCleanupService);

  constructor() {
    inject(PlatformService).isBrowser && this.updateConfig();
  }

  public setItem(key: string, value: unknown, storageType = StorageType.LOCAL, ignorePrefix = false): boolean {
    const storage = this.storageHelper.getStorage(storageType);

    if (!storage) {
      return false;
    }

    storage.setItem(this.getKey(key, ignorePrefix), prepareStorageValue(value));

    return true;
  }

  public getItem<T = never>(key: string, storageType = StorageType.LOCAL, ignorePrefix = false): T {
    const storage = this.storageHelper.getStorage(storageType);

    if (!storage) {
      return null;
    }

    return parseJsonString(storage.getItem(this.getKey(key, ignorePrefix)));
  }

  public removeItem(key: string, storageType = StorageType.LOCAL, ignorePrefix = false): boolean {
    const storage = this.storageHelper.getStorage(storageType);

    if (!storage) {
      return false;
    }

    storage.removeItem(this.getKey(key, ignorePrefix));

    return true;
  }

  public bulkRemove(keys: string[], storageType = StorageType.LOCAL): void {
    const storage = this.storageHelper.getStorage(storageType);

    if (!storage) {
      return;
    }

    keys.forEach(key => this.removeItem(key, storageType));
  }

  /**
   * Remove all keys having a specific prefix. If prefix is not applied,
   * then prefix configured via InjectionToken will be used.
   * @param prefix
   * @param storageType
   */
  public bulkRemovePrefixed(prefix = this.prefix, storageType = StorageType.LOCAL): void {
    const storage = this.storageHelper.getStorage(storageType);

    if (!storage) {
      return;
    }

    getAllPrefixedKeys(storage, prefix).forEach(key => storage.removeItem(key));
  }

  public hasItem(key: string, storageType = StorageType.LOCAL): boolean {
    const storage = this.storageHelper.getStorage(storageType);

    if (!storage) {
      return false;
    }

    return storage.getItem(getPrefixedKey(key, this.prefix)) !== null;
  }

  private updateConfig(): void {
    const logoutKeys = inject(STORAGE_LOGOUT_CLEANUP_KEYS, { optional: true });
    const accountSwitchKeys = inject(STORAGE_ACCOUNT_SWITCH_CLEANUP_KEYS, { optional: true });
    const excludePattern = inject(STORAGE_EXCEPTIONAL_KEY_REGEX, { optional: true });
    const isCleanupConfig = this.prefix && !!(logoutKeys || accountSwitchKeys);

    const storageConfig = new Map(this.getItem<StorageConfig>(STORAGE_CLEANUP_CONFIG_KEY, StorageType.LOCAL, true));
    isCleanupConfig && storageConfig.set(this.prefix, [logoutKeys, accountSwitchKeys, excludePattern?.source]);

    const storageConfigArr = Array.from(storageConfig);
    isCleanupConfig && this.setItem(STORAGE_CLEANUP_CONFIG_KEY, storageConfigArr, StorageType.LOCAL, true);

    this.storageCleanupService.setConfig(storageConfigArr);
  }

  private getKey(key: string, ignorePrefix: boolean): string {
    return ignorePrefix ? key : getPrefixedKey(key, this.prefix);
  }
}
